import type { Config } from '@packages/config/src/default/types';
import { logger } from '../logger/logger';
import { isValidDate } from './isValidDate';

// Intl.DateTimeFormat construction is the expensive part; one formatter per time zone is
// enough because they are stateless.
const formatters = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (timeZone: string) => {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat([], {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
};

/**
 * Converts the given Date object to the given time zone
 * @param timeZone The desired time zone (e.g. "Europe/Amsterdam")
 * @returns New date object for the desired time zone that returns the correct local time hours and minutes
 */
export const toLocalTime = (date: Date, timeZone: Config['timeZone'] = 'Europe/Berlin') => {
  if (!isValidDate(date)) {
    logger.warn({ date }, 'toLocalTime received an invalid date');
    return null;
  }

  const dateObj = getFormatter(timeZone).formatToParts(date);

  const valueFor = (type: string) => dateObj.find((d) => d.type === type)?.value;
  const dateLocalTimeZoneString = `${valueFor('year')}-${valueFor('month')}-${valueFor('day')}T${valueFor('hour')}:${valueFor('minute')}`;

  return new Date(dateLocalTimeZoneString);
};

/**
 * Converts the Date object that "new Date()" generates to the given time zone
 * @param timeZone The desired time zone (e.g. "Europe/Amsterdam")
 * @returns New date object for the desired time zone that returns the correct local time hours and minutes
 */
export const getLocalTime = (timeZone: Config['timeZone'] = 'Europe/Berlin') =>
  // this returns always a Date as new Date() is always valid
  toLocalTime(new Date(), timeZone) as Date;
